import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Share, PlusSquare, Download, PartyPopper } from 'lucide-react';
import { Logo } from './Logo';

const STORAGE_KEY = 'festin_install_prompt_dismissed';
const DELAY_MS = 4000;

function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true
  );
}

export function InstallPrompt() {
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState<'ios' | 'android' | null>(null);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY) || isStandalone()) return;
    if (window.innerWidth >= 768) return; // mobile uniquement

    if (isIos()) {
      const t = setTimeout(() => {
        setPlatform('ios');
        setVisible(true);
      }, DELAY_MS);
      return () => clearTimeout(t);
    }

    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setPlatform('android');
      setTimeout(() => setVisible(true), DELAY_MS);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, []);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, '1');
    setVisible(false);
  };

  const handleAndroidInstall = async () => {
    if (!deferredPrompt) return dismiss();
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    dismiss();
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[300] bg-black/70 backdrop-blur-sm flex items-end md:hidden"
          onClick={dismiss}
        >
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 300, damping: 32 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full bg-[#121212] border-t border-white/10 rounded-t-[2rem] p-6 pb-8"
          >
            <button
              onClick={dismiss}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/5 text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
              aria-label="Fermer"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="relative flex flex-col items-center text-center mb-6">
              <Logo className="text-3xl mb-4" />
              <h2 className="text-xl font-black text-white mb-2">Installe Festin sur ton téléphone</h2>
              <p className="text-sm text-gray-400 max-w-xs">
                Accède à tes candidatures et festivals en un tap, comme une vraie application.
              </p>
            </div>

            {platform === 'ios' ? (
              <div className="space-y-3">
                <div className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-2xl p-4">
                  <span className="shrink-0 w-7 h-7 rounded-full bg-[#ff5a00] text-white text-sm font-black flex items-center justify-center">1</span>
                  <p className="text-sm text-gray-300">
                    Appuie sur <Share className="inline w-4 h-4 mx-0.5 -mt-0.5 text-[#ff5a00]" /> <strong className="text-white">Partager</strong> en bas de Safari
                  </p>
                </div>
                <div className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-2xl p-4">
                  <span className="shrink-0 w-7 h-7 rounded-full bg-[#ff5a00] text-white text-sm font-black flex items-center justify-center">2</span>
                  <p className="text-sm text-gray-300">
                    Fais défiler et choisis <PlusSquare className="inline w-4 h-4 mx-0.5 -mt-0.5 text-[#ff5a00]" /> <strong className="text-white">Sur l'écran d'accueil</strong>
                  </p>
                </div>
                <div className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-2xl p-4">
                  <span className="shrink-0 w-7 h-7 rounded-full bg-[#ff5a00] text-white text-sm font-black flex items-center justify-center">3</span>
                  <p className="text-sm text-gray-300">
                    Appuie sur <strong className="text-white">Ajouter</strong>. Festin devient une vraie app <PartyPopper className="inline w-4 h-4 mx-0.5 -mt-0.5 text-[#ff5a00]" />
                  </p>
                </div>
                <button
                  onClick={dismiss}
                  className="w-full mt-2 bg-[#ff5a00] hover:bg-[#ff6b1a] text-white font-black py-4 rounded-2xl transition-colors"
                >
                  J'ai compris
                </button>
              </div>
            ) : (
              <button
                onClick={handleAndroidInstall}
                className="w-full flex items-center justify-center gap-2 bg-[#ff5a00] hover:bg-[#ff6b1a] text-white font-black py-4 rounded-2xl transition-colors"
              >
                <Download className="w-5 h-5" />
                Installer l'application
              </button>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
